import React, { useEffect, useRef, useState } from "react";
import { McModal, McButton, McInput, McSelect, McOption } from "@maersk-global/mds-react-wrapper/components-core";
import "./AccessRequestForm.css";

const NAME_PATTERN = /^[\p{L}\s'.-]{1,100}$/u;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TABS = [
  { type: "internal", label: "Internal User", domain: "maersk.com", placeholder: "frank@mail.com" },
  { type: "external", label: "External User", domain: "vestas.com", placeholder: "frank@mail.com" },
];

const ROLES_BY_TYPE = {
  internal: [
    { label: "IdaMaerskBilling", value: "IdaMaerskBilling" },
    { label: "IDAMaerskCX", value: "IDAMaerskCX" },
    { label: "IDAMaerskViewer", value: "IDAMaerskViewer" },
  ],
  external: [
    { label: "IDAVestasApprover", value: "IDAVestasApprover" },
    { label: "IDAVestasViewer", value: "IDAVestasViewer" },
  ],
};

const EMPTY_FORM = { firstName: "", lastName: "", email: "", role: "", website: "" };

export default function AccessRequestForm({ open, onClose, registrationUrl }) {
  const modalRef = useRef(null);

  const [requestType, setRequestType] = useState("internal");
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [cooldown, setCooldown] = useState(false);
  // Bumped whenever the fields should be wiped (tab switch, modal reopen) so the
  // key below forces a fresh remount instead of relying on prop diffing — the
  // MDS select in particular only syncs its displayed label on a real value
  // change, so a stale internal value from the previous tab/session made the
  // first click after a reset silently no-op.
  const [resetToken, setResetToken] = useState(0);

  // McButton (custom-element wrapper) can hold on to the onClick handler from
  // its first render instead of rebinding on every re-render, so handleSubmit
  // must read live values through refs rather than closing over state.
  const requestTypeRef = useRef(requestType);
  const formRef = useRef(form);
  const cooldownRef = useRef(cooldown);
  requestTypeRef.current = requestType;
  formRef.current = form;
  cooldownRef.current = cooldown;

  useEffect(() => {
    if (!modalRef.current) return;
    try {
      modalRef.current.open = open;
    } catch {
      modalRef.current.setAttribute("open", "");
    }

    if (open) {
      setForm(EMPTY_FORM);
      setStatusMessage(null);
      setRequestType("internal");
      setCooldown(false);
      setResetToken((token) => token + 1);
    }
  }, [open]);

  const handleFieldChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleTabChange = (type) => {
    setRequestType(type);
    setForm(EMPTY_FORM);
    setStatusMessage(null);
    setResetToken((token) => token + 1);
  };

  const handleRoleSelected = (event) => {
    setForm((prev) => ({ ...prev, role: event.detail?.value ?? "" }));
  };

  const validate = (currentForm, currentRequestType) => {
    if (!currentForm.firstName.trim()) return "First name is required.";
    if (!NAME_PATTERN.test(currentForm.firstName.trim())) return "First name contains characters that aren't allowed.";
    if (!currentForm.lastName.trim()) return "Last name is required.";
    if (!NAME_PATTERN.test(currentForm.lastName.trim())) return "Last name contains characters that aren't allowed.";
    if (!currentForm.email.trim()) return "Email is required.";
    if (!EMAIL_PATTERN.test(currentForm.email.trim())) return "Please enter a valid email address.";
    if (!currentForm.role) return "Please select a requested role.";
    if (!ROLES_BY_TYPE[currentRequestType].some((role) => role.value === currentForm.role)) {
      return "Please select a role for the selected tab.";
    }
    return null;
  };

  const handleSubmit = async () => {
    if (cooldownRef.current) return;

    const currentForm = formRef.current;
    const currentRequestType = requestTypeRef.current;

    setStatusMessage(null);

    const validationError = validate(currentForm, currentRequestType);
    if (validationError) {
      setStatusMessage({ type: "error", text: validationError });
      return;
    }

    setSubmitting(true);

    const payload = {
      request_type: currentRequestType,
      first_name: currentForm.firstName.trim(),
      last_name: currentForm.lastName.trim(),
      email: currentForm.email.trim(),
      role: currentForm.role,
      website: currentForm.website,
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/access-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success) {
        setStatusMessage({ type: "success", text: data.message });
        setForm(EMPTY_FORM);
      } else {
        setStatusMessage({ type: "error", text: data.message });
        if (res.status === 429 || res.status === 403) {
          setCooldown(true);
        }
      }
    } catch {
      setStatusMessage({
        type: "error",
        text: "Service temporarily unavailable. Please try again later.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const activeTab = TABS.find((tab) => tab.type === requestType);

  const isFormIncomplete =
    !form.firstName.trim() ||
    !form.lastName.trim() ||
    !form.email.trim() ||
    !form.role;

  return (
    <McModal ref={modalRef} heading="Request application access" dimension="large" width="520px" closed={onClose}>
      <div className="access-request-form">
        <div className="access-request-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.type}
              type="button"
              role="tab"
              aria-selected={requestType === tab.type}
              className={`access-request-tab ${requestType === tab.type ? "active" : ""}`}
              onClick={() => handleTabChange(tab.type)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {requestType === "external" && (
          <p className="access-request-choice-hint">
            Choose <strong>one</strong> option below: submit the request
            form, or use the self-service registration link.
          </p>
        )}

        <div className="access-request-fields" key={`${requestType}-${resetToken}`}>
          <McInput
            label="First name"
            placeholder="Enter your first name"
            required
            input={handleFieldChange("firstName")}
            maxlength={100}
          />

          <McInput
            label="Last name"
            required
            placeholder="Enter your last name"
            input={handleFieldChange("lastName")}
            maxlength={100}
          />

          <McInput
            label="Email"
            type="email"
            required
            placeholder={activeTab.placeholder}
            input={handleFieldChange("email")}
          />

          <McSelect
            label="Requested role"
            required
            placeholder="Select a role"
            value={form.role}
            optionselected={handleRoleSelected}
          >
            {ROLES_BY_TYPE[requestType].map((role) => (
              <McOption key={role.value} value={role.value}>
                {role.label}
              </McOption>
            ))}
          </McSelect>

          {/* Honeypot — hidden from real users, left off-screen (not display:none) */}
          <input
            type="text"
            name="website"
            autoComplete="off"
            tabIndex="-1"
            aria-hidden="true"
            className="access-request-honeypot"
            value={form.website}
            onChange={handleFieldChange("website")}
          />
        </div>

        {requestType === "external" && (
          <div className="access-request-divider">
            <span className="access-request-divider-line" />
            <span className="access-request-divider-text">OR</span>
            <span className="access-request-divider-line" />
          </div>
        )}

        {requestType === "external" && (
          <div className="access-request-register">
            <span>Prefer to self-register instead?</span>
            <a href={registrationUrl || "#"} target="_blank" rel="noreferrer">
              Register here
            </a>
          </div>
        )}

        {statusMessage && (
          <p className={`access-request-status access-request-status--${statusMessage.type}`}>
            {statusMessage.text}
          </p>
        )}
      </div>

      <div slot="footer" className="access-request-actions">
        <McButton appearance="primary" onClick={handleSubmit} fit='small' disabled={submitting || cooldown || isFormIncomplete}>
          {submitting ? "Submitting..." : "Submit request"}
        </McButton>
        <McButton appearance="secondary" onClick={onClose} fit='small' disabled={submitting}>
          Cancel
        </McButton>
      </div>
    </McModal>
  );
}
